import React, {forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState,} from "react";
import {GeolocationError} from "ol/Geolocation";
import debounce from "lodash.debounce";

import {IClusterPlanLongItem, TShippingGeo} from "../../../api/apiTypes";
import {Controller, CustomDraw, CustomOverlay, OpenLayers, useCluster} from "@delivus/react-open-layers";
import {PlansTooltip,} from "./PlansTooltip";
import {PlanPolygon} from "../../../assets/svgs/planPolygon";
import {PlanSector} from "../../../assets/svgs/planSector";
import {Geolocation} from "../../../assets/svgs/geolocation";
import {Map} from "ol";
import Feature, {FeatureLike} from "ol/Feature";
import VectorSource from "ol/source/Vector";
import {isUpdateDisabled} from "../../../common/fns";
import {useTranslation} from "react-i18next";
import {useSectors} from "../../../api/useSectors";
import {HoveredShippingTooltipType} from "../../screens/shipping/ShippingMapScreen";
import {PlanDelete} from "../../../assets/svgs/planDelete";
import {useZipcodes} from "../../../api/useZipcodes";
import {colorsHex, DATE_FORMAT} from "../../../common/consts.common";
import {PlanRefresh} from "../../../assets/svgs/planRefresh";
import {ZoomSlider} from "../../molecules/zoomSlider/ZoomSlider";
import {showApiError, showMessage} from "../../../common/message";
import dayjs from "dayjs";
import {useSearchParams} from "react-router-dom";
import {Button, message} from "antd";
import {TMarkerProperty, useMapFunctions} from "../../molecules/map/useMapFunctions";
import {Point} from "ol/geom";
import {Fill, Stroke, Style, Text} from "ol/style";
import TileLayer from "ol/layer/Tile";
import {XYZ} from "ol/source";

export const MIN_ZOOM = 10;
export const MAX_ZOOM = 18;
const ZOOM = 13;
const now = dayjs();
export type TPlanMarkerProperty = {
  hovered?: boolean;
  disabled?: boolean;
  highlighted: boolean;
  shipping?: IShippingPlanMarker;
  color?: string;
}

export interface IShippingPlanMarker extends TShippingGeo {
  cluster_id: string;
}

interface Props {
  onSelectShippings: (shippings: IShippingPlanMarker[]) => void;
  onReset: () => void;
}

type TPolygonCache = {
  [key: string]: Style
};

const DARK = "#000000";
const PlanMap = forwardRef(({onSelectShippings, onReset: onResetProp}: Props, ref) => {
  const {t} = useTranslation(["shipping"]);
  const [messageApi, messageHolder] = message.useMessage();
  const centerValue = useRef<any>();
  const drawnPolygonsRef = useRef<Feature[]>([]);
  const [hoveredShippingTooltip, setHoveredShippingTooltip] = useState<HoveredShippingTooltipType<IShippingPlanMarker>>();
  const [bound, setBound] = useState<Array<number>>();
  const [map, setMap] = useState<Map>();
  const [isVisibleTooltip, setIsVisibleTooltip] = useState(true);
  const [zoom, setZoom] = useState(ZOOM);
  const [isVisiblePolygon, setVisiblePolygon] = useState(false);
  const [isVisibleSector, setVisibleSector] = useState(false);
  const [isVisibleZipcode, setVisibleZipcode] = useState(false);
  const [isVisibleMarker, setVisibleMarker] = useState(true);
  const [isEnableGeolocation, setGeolocation] = useState(false);
  const [markers, setFeatures] = useState<Feature[]>([]);
  const drawSource = useRef<VectorSource>();
  const clusterSource = useRef<VectorSource>();
  const [_, setSearchParams] = useSearchParams({plan_date: now.format(DATE_FORMAT)});
  const polygonCache = useRef<TPolygonCache>({});
  const unusedColorsRef = useRef([...colorsHex]); // copy
  
  const onReset = useCallback(() => {
    drawnPolygonsRef.current && drawnPolygonsRef.current.forEach((feature) => {
      drawSource.current && drawSource.current.removeFeature(feature);
    });
    onResetProp();
  }, [onResetProp]);
  
  const sectorStyle = useCallback((feature: FeatureLike) => {
    const code = feature.get("code") || "";
    console.log("polygonStyle", code, !polygonCache.current[code])
    if (!polygonCache.current[code]) {
      const color = getColor(unusedColorsRef.current, code, true);
      polygonCache.current[code] = new Style({
        stroke: new Stroke({
          color, width: 2.5,
        }), fill: new Fill({
          color: color + "25",
        })
      })
    }
    return polygonCache.current[code];
  }, []);
  
  const zipcodeStyle = useCallback((feature: any) => {
    const zipcode = feature.get("zipcode") || "";
    if (!polygonCache.current[zipcode]) {
      polygonCache.current[zipcode] = new Style({
        stroke: new Stroke({
          color: DARK, width: 1,
        }), fill: new Fill({
          color: DARK + "10",
        }), text: new Text({
          font: "12px Calibri,sans-serif", fill: new Fill({color: "#000"}), stroke: new Stroke({
            color: "#fff", width: 2,
          }), // get the text from the feature - `this` is ol.Feature
          // and show only under certain resolution
          text: zipcode,
        }),
      })
    }
    return polygonCache.current[zipcode];
  }, []);
  
  const {
          handleClickCluster,
          handleMouseOverCluster,
          handleReset,
          addToSelectedShippings,
          getSelectedShippings,
          clusterOverStyle,
          clusterStyle,
          getColor
        } = useMapFunctions<IShippingPlanMarker>(onSelectShippings, onReset, messageApi);
  
  useSectors(bound, map, isVisibleSector, sectorStyle);
  useZipcodes(bound, map, isVisibleZipcode, zipcodeStyle);
  
  useEffect(() => {
    drawSource.current = undefined;
    drawnPolygonsRef.current = [];
  }, [isVisiblePolygon]);
  
  useImperativeHandle(ref, () => ({
    setPlans: createMarkerFromPlans, reset: handleReset,
  }), []);
  
  useCluster({
    features: markers,
    map,
    zoom,
    onClick: handleClickCluster,
    onOver: (selected, deselected, event) => handleMouseOverCluster(selected, deselected, event, setHoveredShippingTooltip, setHoveredShippingTooltip),
    clusterStyle: clusterStyle,
    overStyle: clusterOverStyle,
    layerOptions: {zIndex: 15}
  });
  
  const createMarkerFromPlans = useCallback((clusterPlans: IClusterPlanLongItem[]) => {
    let features: Feature[] = [];
    if (clusterPlans) {
      clusterPlans.forEach((cluster) => {
        if (cluster.shippingitems_geo) {
          cluster.shippingitems_geo.forEach((shipping) => {
            shipping["cluster_id"] = cluster.cluster_id;
            const disabled = isUpdateDisabled(shipping.shipping_item__status, shipping.is_scanned);
            const feature = new Feature({
              name: cluster.cluster_id, geometry: new Point([
                shipping.shipping_item__address__lng, shipping.shipping_item__address__lat,
              ]), shipping, disabled
            })
            features.push(feature);
          });
        }
      });
      setFeatures(features);
    }
  }, []);
  
  const handleMapBoundChanged = useCallback(debounce((event: any) => {
    const map = event.map;
    if (map) {
      const view = map.getView();
      const newZoom = view.getZoom();
      const newCenter = view.getCenter();
      console.log("handleMapBoundChanged actually moved zoom", newZoom);
      setZoom(newZoom);
      if (newZoom < 13) setVisibleZipcode(false);
      if (newZoom < 14) {
        setVisibleSector(false);
      }
      else {
        centerValue.current = newCenter;
      }
      const projection = map.getView()
                            .getProjection();
      console.log("Projection:", projection.getCode());
      const extent = view.calculateExtent(map.getSize());
      console.log("extent", extent)
      setBound(extent);
    }
  }, 300), []);
  
  const handleZipcodeVisible = () => {
    if (!isVisibleZipcode && zoom < 13) {
      showMessage(messageApi, t("The Zipcode layer can be visible when the zoom level exceeds 13"));
      return;
    }
    setVisibleZipcode((prev) => !prev);
  };
  
  const handleSectorVisible = () => {
    if (!isVisibleSector && zoom < 14) {
      showMessage(messageApi, t("The Sector layer can be visible when the zoom level exceeds 14"));
      return;
    }
    setVisibleSector((prev) => !prev);
  };
  
  const handleRefresh = useCallback(() => {
    setSearchParams((prev) => ({
      ...prev
    }));
  }, [setSearchParams]);
  
  const handleDrawEnd = useCallback((event: any) => {
    drawnPolygonsRef.current.push(event.feature);
    const extents = event.feature.getGeometry()
                         .getExtent();
    if (clusterSource.current) {
      const features = clusterSource.current.getFeaturesInExtent(extents);
      features.forEach((f: Feature) => {
        const properties = f.getProperties() as TMarkerProperty;
        if (properties.shipping && !properties.highlighted) {
          addToSelectedShippings(f, properties);
        }
      });
      const shippings = getSelectedShippings();
      onSelectShippings(shippings);
    }
  }, [addToSelectedShippings, getSelectedShippings, onSelectShippings]);
  
  const topControlNode = (<>
    <Button
      type={"primary"}
      size={"small"}
      className={"button " + (isVisibleZipcode)}
      onClick={handleZipcodeVisible}
    >
      <PlanSector color={(isVisibleZipcode) ? "white" : "black"} />
      {t("zipcode layer")}
    </Button>
    <Button
      type={"primary"}
      size={"small"}
      className={"button " + (isVisibleSector)}
      onClick={handleSectorVisible}>
      <PlanSector color={(isVisibleSector) ? "white" : "black"} />
      {t("sector layer")}
    </Button>
    <Button
      type={"primary"}
      size={"small"}
      className={"button " + isVisiblePolygon}
      onClick={() => setVisiblePolygon((prev) => !prev)}>
      <PlanPolygon color={isVisiblePolygon ? "white" : "black"} />
      {t("polygon draw")}
    </Button>
    <Button
      type={"primary"}
      size={"small"}
      className={"button " + isVisibleMarker}
      onClick={() => setVisibleMarker((prev) => !prev)}>
      <PlanPolygon color={isVisibleMarker ? "white" : "black"} />
      {t("marker layer")}
    </Button>
    <Button
      type={"primary"}
      size={"small"}
      className={"button " + isVisibleTooltip}
      onClick={() => setIsVisibleTooltip((prev) => !prev)}>
      <PlanPolygon color={isVisibleTooltip ? "white" : "black"} />
      {t("tooltip")}
    </Button>
    
    <Button
      type={"primary"}
      size={"small"}
      className={"button false"}
      onClick={handleReset}
    >
      <PlanDelete color={"black"} />
    </Button>
    <Button
      type={"primary"}
      size={"small"}
      className={"button false"}
      onClick={handleRefresh}>
      <PlanRefresh color={"black"} />
    </Button>
  </>);
  const handleGeoLocationError = (error: GeolocationError) => {
    showApiError(error, messageApi);
    setGeolocation(false);
  };
  const vworldBaseLayer = new TileLayer({
    source: new XYZ({
      url: `http://api.vworld.kr/req/wmts/1.0.0/${process.env.REACT_APP_MAP_KEY}/white/{z}/{y}/{x}.png`,
    }), properties: {name: "base-vworld-base"}, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM, // zIndex: 2,
    preload: Infinity,
  });
  
  return (<div className={"map-container"}>
    <OpenLayers
      // layers={[vworldBaseLayer]}
      interactionOptions={{pinchZoom: true}}
      initialCenter={[126.83, 37.57]}
      onMoveEnd={handleMapBoundChanged}
      showZoom
      zoom={zoom}
      onInit={setMap}
      geolocationOptions={{
        fillColor: "#6e6eff", onError: handleGeoLocationError,
      }}
      showGeolocation={isEnableGeolocation}
      viewOptions={{
        zoom: ZOOM, maxZoom: MAX_ZOOM, minZoom: MIN_ZOOM,   // smaller = smoother zoom units
        projection: 'EPSG:4326', //Use [lon, lat] directly
      }}
    >
      {messageHolder}
      <Controller
        id={"top-control"}
        className={"float top left"}
      >
        {topControlNode}
      </Controller>
      <Controller id={"right-control"} className={"float top right"}>
        <Button
          type={"primary"}
          size={"small"}
          className={"button " + isEnableGeolocation}
          onClick={() => setGeolocation((prev) => !prev)}>
          <Geolocation color={isEnableGeolocation ? "white" : "black"} />
        </Button>
      </Controller>
      <Controller id={"slider-control"} className={"float slider right"}>
        <ZoomSlider
          zoom={zoom}
          onChange={setZoom}
          max={MAX_ZOOM}
          min={MIN_ZOOM}
          defaultValue={ZOOM}
        />
      </Controller>
      {isVisiblePolygon && (<CustomDraw
        onSourceCreated={(source) => (drawSource.current = source)}
        onDrawEnd={handleDrawEnd}
      />)}
      <CustomOverlay
        id={"shipping-tooltip"}
        className={"tooltip"}
        position={hoveredShippingTooltip?.position}
      >
        {isVisibleTooltip ? (<PlansTooltip shippings={hoveredShippingTooltip?.shippings} />) : undefined}
      </CustomOverlay>
    </OpenLayers></div>);
});

export default memo(PlanMap);
